import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { guacHttpClient } from "@/lib/http";
import { intakeNormalize } from "@/lib/intakeNormalize";
import {
  CreateJobIntakeRequest,
  HttpResponse,
  JobIntakeWithCompletion,
} from "@/types";

export function useCreateJobIntake() {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();

  const jobIntakeRequestObject: CreateJobIntakeRequest = {
    generalInfo: {
      roleTitle: null,
      compensationMinListed: null,
      compensationMaxListed: null,
      employmentType: null,
      shiftPreferred: null,
      shiftFlexible: null,
      quantityNeeded: null,
      quantityMaximum: null,
      absoluteMaxCompensation: null,
      shiftDifferentialMin: null,
      shiftDifferentialMax: null,
      benefits: null,
      physicalWorkConditions: null,
      openToRelocation: null,
      relocationDetails: null,
      relocationPackageMin: null,
      relocationPackageMax: null,
      backgroundCheckRequired: null,
      backgroundAndDrugTestDetails: null,
      drugTestRequired: null,
      overtimeAvailable: null,
      overtimeAvailability: null,
      overtimeHoursPerWeek: null,
      overtimePayMultiplier: null,
      overtimeDetails: null,
      yearsOfExperiencePreferred: null,
      details: null,
      siteId: null,
    },
    talentRecruitingHistory: {
      openDuration: null,
      fillTimeUsually: null,
      areOtherStaffingFirmsWorking: false,
      willOtherStaffingFirmsWork: false,
      isInternalTeamRecruiting: false,
      struggles: null,
      numberOfCandidatesSubmitted: null,
      numberOfInterviews: null,
      extraContext: null,
      customersFrustrations: null,
    },
    culture: {
      teamDescription: null,
      managerDetails: null,
      successfulTraitsAndPersonalities: null,
      failingBehaviorsAndPersonalities: null,
    },
    idealCandidateProfiles: [],
    antiIdealCandidateProfiles: [],
    laborupRound: {
      submissionProcess: null,
      additionalExpectations: null,
    },
    interviewProcess: [],
    datesAndUrgency: {
      deadlineFirstBatch: null,
      deadlineFirstPlacement: null,
      deadlineJobFinished: null,
      assumptions: null,
      details: null,
    },
    screeningQuestions: {
      dealBreakers: null,
      contextBuilders: null,
    },
    attachments: [],
  };

  return useMutation({
    mutationFn: async () => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/job-intake`;
      const res = await guacHttpClient.post<
        JobIntakeWithCompletion,
        HttpResponse<JobIntakeWithCompletion>,
        CreateJobIntakeRequest
      >(path, jobIntakeRequestObject);
      return { ...res.data, jobIntake: intakeNormalize.job(res.data.jobIntake) };
    },
    onSuccess: (data) => {
      // Seed the detail cache with the freshly-computed intake + completion.
      queryClient.setQueryData(
        ["job-intake", currentCompanyId, currentJobId],
        data,
      );

      // Invalidate queries that have job intake
      void queryClient.invalidateQueries({
        queryKey: ["jobs", currentCompanyId],
      });
    },
  });
}
